"use client";

import React, { useState } from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DotsBackdrop from "@/components/lp-long/DotsBackdrop";
import { cn } from "@/lib/utils";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { PageLPLongSchema } from "@/sanity/pages/PageLPLongTemplate";

import wikis from "@/public/lp-long/wikis.svg";

// column accent colors: TradeMania, Coachings, Signalgruppen
const COLUMN_COLORS = ["#D9F9B9", "#E9B9F9", "#B9C0F9"];
const VALUE_KEYS = ["col2", "col3", "col4"] as const;

// top-right corner cut, same language as CornerChip / corner buttons
const CORNER_CUT = "[clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]";

export default function TableSection({ data, className }: { data: PageLPLongSchema; className?: string }) {
  const [active, setActive] = useState(0);

  const heads = [data?.comparisonTableHead1, data?.comparisonTableHead2, data?.comparisonTableHead3];
  const rows = data?.comparisonTable ?? [];

  return (
    <section className={cn("relative overflow-clip pt-[4rem] pb-[8rem] lg:pt-[15rem] lg:pb-[12rem]", className)}>
      <DotsBackdrop
        className="-top-[1.25rem] hidden lg:block"
        width="59.9375rem"
        height="12.625rem"
      />
      <DecorationLine
        className="top-0 hidden lg:block"
        orientation="horizontal"
        style="dark"
      />
      <DecorationLine
        className="top-0 hidden lg:left-[8.85%] lg:block"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 hidden lg:right-[8.85%] lg:block"
        orientation="vertical"
        style="dark"
      />

      <div className="relative container flex flex-col items-center">
        <Chip
          className="mb-[2rem] lg:mb-[3rem] prose-p:font-geistmono prose-p:text-[#202223] prose-p:uppercase"
          image={data?.comparisonChip?.icon}
          text={data?.comparisonChip?.text}
        />

        {data?.comparisonTitle && (
          <div className="richtext prose-headings:mb-0 prose-headings:w-[min(70rem,100%)]">
            <PortableText
              value={data.comparisonTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {/* ───────── desktop: 4-column table ───────── */}
        {!!rows.length && (
          <div className="relative mt-[9.5rem] hidden w-[min(150rem,100%)] lg:block">
            {/* highlight behind the TradeMania column */}
            <div
              className="pointer-events-none absolute top-[14rem] bottom-0 left-[38rem] w-[37.625rem] bg-[#D9F9B9]/20"
              aria-hidden
            />

            {/* column heads */}
            <div className="ml-[37.95rem] flex gap-[1rem]">
              {heads.map((head, i) => (
                <div
                  key={i}
                  className={cn(
                    "relative flex flex-col items-center gap-[1.5rem] bg-white py-[4rem] text-center",
                    i === 0 ? "w-[37.625rem] shrink-0" : "flex-1",
                  )}>
                  {i === 0 && (
                    <>
                      <div
                        className="absolute bottom-full left-0 h-[2px] w-[45.375rem] bg-[#D9F9B9]"
                        aria-hidden
                      />
                      <div
                        className={cn(
                          "absolute bottom-[calc(100%+2px)] left-0 flex h-[5rem] items-center gap-[1rem] bg-[#D9F9B9] pl-[1.5rem] pr-[2rem]",
                          CORNER_CUT,
                        )}>
                        <Image
                          className="!w-[3rem]"
                          src={wikis}
                          alt=""
                        />
                        <span className="font-geistmono text-[2rem] leading-none font-medium text-[#0A1A1D] uppercase">
                          Best option
                        </span>
                      </div>
                    </>
                  )}
                  <p className="mb-0 w-full font-funnel text-[3rem] leading-[1.4] font-medium text-black/80">
                    {head?.name}
                  </p>
                  <p className="mb-0 w-full text-[2.5rem] leading-none text-black/60">{head?.text}</p>
                </div>
              ))}
            </div>

            {/* rows */}
            <div className="relative mt-[5.75rem] flex flex-col gap-[3rem] pb-[3rem]">
              {rows.map((row, r) => (
                <React.Fragment key={r}>
                  {r > 0 && (
                    <div
                      className="h-px w-full bg-black/10"
                      aria-hidden
                    />
                  )}
                  <div className="flex items-center gap-[13.125rem]">
                    <p className="mb-0 w-[25rem] shrink-0 font-geistmono text-[2.25rem] leading-[1.5] text-black/60 uppercase">
                      {row?.col1}
                    </p>
                    <div className="flex flex-1 items-center gap-[2rem] pl-[2rem]">
                      <p className="mb-0 w-[40.5rem] shrink-0 text-[2.25rem] leading-[1.5] font-medium text-black/90">
                        {row?.col2}
                      </p>
                      <p className="mb-0 flex-1 text-[2.25rem] leading-[1.5] text-black/60">{row?.col3}</p>
                      <p className="mb-0 flex-1 text-[2.25rem] leading-[1.5] text-black/60">{row?.col4}</p>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* ───────── mobile: one column, chips switch it ───────── */}
        {!!rows.length && (
          <div className="mt-[9rem] w-full lg:hidden">
            <div className="relative flex flex-col gap-[4rem] bg-white p-[3rem]">
              {/* column chips + accent line, sitting on the card's top edge */}
              <div className="absolute top-0 left-0 flex w-full -translate-y-full flex-col">
                <div
                  className="flex gap-[1rem]"
                  role="tablist">
                  {heads.map((head, i) => (
                    <button
                      key={i}
                      type="button"
                      role="tab"
                      aria-selected={i === active}
                      onClick={() => setActive(i)}
                      className={cn(
                        "flex h-[4.75rem] items-center px-[1.875rem] font-geistmono text-[1.75rem] font-medium uppercase leading-none text-[#0A1A1D] transition-opacity",
                        CORNER_CUT,
                        i !== active && "opacity-60",
                      )}
                      style={{ backgroundColor: COLUMN_COLORS[i] }}>
                      {head?.shortName || head?.name}
                    </button>
                  ))}
                </div>
                <div
                  className="h-[2px] w-full transition-colors"
                  style={{ backgroundColor: COLUMN_COLORS[active] }}
                  aria-hidden
                />
              </div>

              <div className="flex flex-col gap-[1.5rem]">
                <p className="mb-0 font-funnel text-[3rem] leading-[1.4] font-medium text-black/80">
                  {heads[active]?.name}
                </p>
                <p className="mb-0 text-[2.5rem] leading-none text-black/60">{heads[active]?.text}</p>
              </div>

              <div className="flex flex-col gap-[2rem]">
                {rows.map((row, r) => (
                  <React.Fragment key={r}>
                    {r > 0 && (
                      <div
                        className="h-px w-full bg-black/10"
                        aria-hidden
                      />
                    )}
                    <div className="flex items-center justify-between gap-[2rem]">
                      <p className="mb-0 w-[22.125rem] shrink-0 font-geistmono text-[2.25rem] leading-[1.5] text-black/30 uppercase">
                        {row?.col1}
                      </p>
                      <p className="mb-0 flex-1 text-[2.25rem] leading-[1.5] text-black/70">
                        {row?.[VALUE_KEYS[active]]}
                      </p>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {data?.comparisonCTA && (
          <CTACornerButton
            className="mt-[4rem] lg:mt-[9.5rem]"
            data={data.comparisonCTA}
          />
        )}
      </div>
    </section>
  );
}
